import dynamic from 'next/dynamic';
import NextLink from 'next/link';

import Bathtub from '@/icons/light/bathtub.svg';
import Bedroom from '@/icons/light/bedroom.svg';
import Garaje from '@/icons/light/garaje.svg';
import Mueble from '@/icons/light/mueble.svg';
import Otros from '@/icons/light/otros.svg';
import Piscina from '@/icons/light/piscina.svg';
import Scale from '@/icons/light/Scale.svg';
import Sun from '@/icons/light/sun.svg';
import { Container, Link, RichInline } from '@/atoms';
import { Gallery } from '@/molecules';
import { FlatDescription } from '@/organisms';

import Background from '@/assets/images/background.jpg';
import Zone from '@/assets/images/zonas.jpg';
import { flatName, zonePath } from '@/components/algolia/utils';
import { generatedListingConfig } from '@/lib/contexts/listing-config.generated';
import { formatNumberToCurrency } from '@/utils';
import { FlatSheetHero } from './FlatSheetHero';

import type { TypeFlatAnalytics } from '@/types';
import type { FunctionComponent } from 'react';
import type { TypeFlatSheetProps } from './types';

const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

/**
 * FlatDescription
 */
export const FlatSheet: FunctionComponent<TypeFlatSheetProps> = ({
  property,
  beforeFeatures,
  contactEyebrow,
  contactTitle,
}) => {
  const navigateToFirstHit = () => zonePath(property, property?.ventaAlquiler);
  const fallbackAlt = property
    ? `${property?.tipoDeUsoDescription} de ${property?.habitaciones} ${
        (property?.habitaciones as number) > 1 ? 'habitaciones' : 'habitación'
      } en ${
        (property?.ciudad as string)[0].toUpperCase() +
        (property?.ciudad as string).substring(1).toLowerCase()
      }`
    : 'Vivienda';
  // Local-only escape hatch — ver comentario en card.ts.
  const FORCE_FALLBACK =
    process.env.NEXT_PUBLIC_FORCE_PHOTO_FALLBACK === 'true';
  const fotosToDisplay = FORCE_FALLBACK
    ? ([] as { src: string; alt: string }[])
    : ((property?.fotos ?? [])
        .map((itm: any) =>
          itm?.url ? { src: itm.url, alt: fallbackAlt } : null,
        )
        .filter(Boolean) as { src: string; alt: string }[]);
  const isForSale = property?.ventaAlquiler?.toLowerCase() === 'venta';

  const useHero = generatedListingConfig.viviendaPageStyle === 'hero';

  return property ? (
    <>
      {useHero ? (
        <FlatSheetHero property={property} />
      ) : (
        <>
          <Container wrapper>
            <div className="flex justify-between">
              <NextLink
                href={navigateToFirstHit()}
                passHref
                prefetch={false}
                legacyBehavior
              >
                <Link icon="left" className="font-medium">
                  Listado
                </Link>
              </NextLink>
            </div>
          </Container>

          <Container wrapper padding="px-4">
            <Gallery
              flat={{
                codigoUnidadAlquiler: property?.codigoUnidadAlquiler as string,
                ventaAlquiler:
                  property?.ventaAlquiler as TypeFlatAnalytics['ventaAlquiler'],
                reservable: property?.reservable,
                visitable: property?.visitable,
                nombreUE: property?.nombreUE as string,
                cityArea: property?.tipoZona?.infoAdicional,
              }}
              images={
                !fotosToDisplay ||
                (fotosToDisplay && fotosToDisplay.length === 0)
                  ? [
                      {
                        alt: 'Imagen de ejemplo',
                        src: Background.src,
                      },
                      { src: Zone.src, alt: 'ejemplo2' },
                      { src: Background.src, alt: 'ejemplo3' },
                    ]
                  : fotosToDisplay.length === 1
                    ? [
                        ...fotosToDisplay,
                        { src: Zone.src, alt: 'ejemplo2' },
                        { src: Background.src, alt: 'ejemplo3' },
                      ]
                    : fotosToDisplay.length === 2
                      ? [
                          ...fotosToDisplay,
                          { src: Background.src, alt: 'ejemplo3' },
                        ]
                      : fotosToDisplay
              }
              video={property?.video?.infoAdicional}
              typeModal="gallery"
            />
          </Container>
        </>
      )}

      <Container
        wrapper
        justify="justify-around"
        classNameWrapper="flex-row! gap-6"
        className={useHero ? 'pt-12 pb-20 lg:pt-20 lg:pb-40' : undefined}
      >
        <FlatDescription
          viewStyle={useHero ? 'hero' : 'legacy'}
          {...(property?.virtualView?.infoAdicional && {
            has360: true,
            src360: property?.virtualView?.infoAdicional,
          })}
          {...(property?.video?.infoAdicional && {
            hasVideo: true,
            srcVideo: property?.video?.infoAdicional,
          })}
          title={flatName(property)}
          price={formatNumberToCurrency(
            isForSale
              ? (property?.importeVenta as number)
              : (property?.importeRecibofinal as number),
          )}
          isPeriodic={!isForSale}
          registroPropiedad={property?.registroPropiedad as string}
          description={{
            title: 'Descripción del alojamiento',
            // @ts-ignore
            content: property?.informacionGeneral?.argumentario
              ?.replaceAll('##', '<br><br>')
              ?.replaceAll('•,,', '<br>&nbsp;•&nbsp;&nbsp;&nbsp;'),
          }}
          {...(property?.otrosDatos?.argumentario && {
            otrosDatos: property.otrosDatos.argumentario
              ?.replaceAll('##', '<br><br>')
              ?.replaceAll('•,,', '<br>&nbsp;•&nbsp;&nbsp;&nbsp;'),
          })}
          flat={{
            codigoUnidadAlquiler: property?.codigoUnidadAlquiler as string,
            ventaAlquiler: property?.ventaAlquiler as string,
            reservable: property?.reservable as string,
            visitable: property?.visitable as string,
            nombreUE: property?.nombreUE as string,
          }}
          beforeFeatures={beforeFeatures}
          features={{
            title: 'Características',
            features: property?.caracteristicas,
          }}
          highlightedFeatures={[
            ...(property?.metrosComputablesUA
              ? [
                  {
                    Icon: Scale,
                    value: `${property?.metrosComputablesUA} m²`,
                  },
                ]
              : []),
            ...(property?.habitaciones
              ? [
                  {
                    Icon: Bedroom,
                    value: `${property?.habitaciones} Habs.`,
                  },
                ]
              : []),
            ...(property?.banos
              ? [
                  {
                    Icon: Bathtub,
                    value: `${property?.banos} baños`,
                  },
                ]
              : []),
            ...(property?.hasAmueblado !== 'No'
              ? [
                  {
                    Icon: Mueble,
                    value: 'Amueb.',
                  },
                ]
              : []),
            ...(property?.hasPiscina !== 'No'
              ? [
                  {
                    Icon: Piscina,
                    value: 'Piscina',
                  },
                ]
              : []),
            ...(property?.hasGaraje !== 'No'
              ? [
                  {
                    Icon: Garaje,
                    value: 'Garaje',
                  },
                ]
              : []),
            ...(property?.hasTerrazas !== 'No'
              ? [
                  {
                    Icon: Sun,
                    value: 'Terraza',
                  },
                ]
              : []),
            ...(property?.hasTraseros !== 'No'
              ? [
                  {
                    Icon: Otros,
                    value: 'Trastero',
                  },
                ]
              : []),
          ]}
          {...(property?.latitud && property?.longitud
            ? {
                location: {
                  title: 'Ubicación',
                  location: {
                    lat: parseFloat(property?.latitud as string),
                    lng: parseFloat(property?.longitud as string),
                  },
                },
              }
            : {})}
          availableDates={property.fechasDisponibles}
        />

        <div
          className={`rounded-custom hidden h-full w-[450px] shrink-0 p-6 lg:sticky lg:top-6 lg:z-10 lg:block ${
            useHero
              ? 'contact-form-scope bg-neutrals-0 drop-shadow-[0px_8px_16px_rgba(0,0,0,0.16)]'
              : 'bg-neutrals-50'
          }`}
        >
          {useHero && (contactEyebrow || contactTitle) && (
            <div className="mb-6 flex flex-col items-end self-start">
              {contactEyebrow && (
                <span className="font-accent text-primary-500 text-3xl leading-10">
                  {contactEyebrow}
                </span>
              )}
              {contactTitle && (
                <RichInline
                  as="h3"
                  html={contactTitle}
                  className="text-headline text-headline-h4 text-text-primary self-start font-bold"
                />
              )}
            </div>
          )}
          <FormsTabs
            noTitle={useHero}
            flat={{
              codigoUnidadAlquiler: property?.codigoUnidadAlquiler,
              ventaAlquiler: property?.ventaAlquiler,
              reservable: property?.reservable,
              visitable: property?.visitable,
              nombreUE: property?.nombreUE,
              cityArea: property?.tipoZona?.infoAdicional,
              name: flatName(property),
              maxOcupantes: property?.maxOcupantes,
            }}
          />
        </div>
      </Container>
    </>
  ) : null;
};

FlatSheet.displayName = 'FlatSheet';
